import { useState, useEffect } from 'react';
import { getUsersByRole } from '@/lib/firebase/queries/users';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '@/lib/firebase/config';
import { useUserCache } from './useUserCache';

interface AdminStats {
  totalCoaches: number;
  totalCustomers: number;
  activeCustomers: number;
  pendingAppointments: number;
}

export function useAdminStats() {
  const [stats, setStats] = useState<AdminStats>({
    totalCoaches: 0,
    totalCustomers: 0,
    activeCustomers: 0,
    pendingAppointments: 0,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { getUsersByRole: getCachedUsersByRole, loading: cacheLoading } = useUserCache({
    ttl: 5 * 60 * 1000, // 5 minutes cache
    enableRealtime: true
  });

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      setLoading(true);
      setError(null);

      // Use cached user data and fetch appointments in parallel
      const [coaches, customers, appointmentsSnapshot] = await Promise.all([
        getCachedUsersByRole('coach'),
        getCachedUsersByRole('customer'),
        getDocs(query(
          collection(db, 'appointments'),
          where('status', '==', 'pending')
        ))
      ]);
      
      // Calculate active customers (active in last 30 days)
      const thirtyDaysAgo = new Date();
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
      
      const activeCustomers = customers.filter(customer => {
        const lastLogin = customer.lastLoginAt?.toDate?.() || customer.lastLoginAt;
        const created = customer.createdAt?.toDate?.() || customer.createdAt;
        
        return (lastLogin && lastLogin > thirtyDaysAgo) || 
               (created && created > thirtyDaysAgo);
      }).length;

      setStats({
        totalCoaches: coaches.length,
        totalCustomers: customers.length,
        activeCustomers,
        pendingAppointments: appointmentsSnapshot.size,
      });
    } catch (err) {
      console.error('Error fetching admin stats:', err);
      setError('Failed to load dashboard statistics');
    } finally {
      setLoading(false);
    }
  };

  return {
    stats,
    loading,
    error,
    refetch: fetchStats,
  };
} 